/**
 * @fileoverview System Users grid page with full invite lifecycle management.
 * @module system-web/pages/UsersPage
 *
 * Responsibilities:
 * - Render the system users grid with status filters, row actions, and bulk actions.
 * - Provide send, resend, and revoke invite actions (single + bulk) from the grid
 *   and the three-dots menu, mirroring tenant-web UsersPage.
 * - Show invite feedback banners and route to the detail slide-out panel.
 *
 * Dependencies:
 * - @ui-admin (GridPageShell, DataGrid types)
 * - systemUserService (local-first reads + invite lifecycle)
 * - UserSlideoutPanel (detail slide-out)
 * - EditSystemUserModal (create / edit modal)
 *
 * Architecture:
 * - All reads come from Dexie via watchSystemUsers/listSystemUsers.
 * - Invite delivery, resend, and revoke are inherently-online exceptions.
 *
 * Notes:
 * - Bulk actions mirror the tenant-web pattern: "Send Invitations" for
 *   not_invited users, "Resend Invitations" for invited users.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Plus, Eye, Pencil, Send, Shield, CheckCircle, AlertTriangle, X,
  RefreshCw,
} from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type {
  DataGridColumn,
  DataGridFilter,
  DataGridRowAction,
  BulkAction,
  GridTemplate,
} from '@ui-admin/components/DataGrid';
import {
  listSystemUsers,
  watchSystemUsers,
  resendSystemInvitation,
  sendSystemUserInvitation,
  bulkSendInvitations,
  bulkResendInvitations,
  deriveUserStatus,
  type BackgroundInviteResult,
} from '../services/systemUserService';
import UserSlideoutPanel from '../components/UserSlideoutPanel';
import EditSystemUserModal from './EditSystemUserModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SystemUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  title?: string;
  position?: string;
  status: string;
  systemRole?: { roleId: string; roleName: string };
  departmentMemberships?: { departmentId: string; departmentName: string; roleName: string; status: string }[];
  organizationMemberships?: { organizationId: string; organizationName: string; roleName: string; status: string }[];
  invite?: any;
  invitationHistory?: any[];
  createdAt?: string;
  lastLoginAt?: string;
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function userName(u: SystemUser): string {
  return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
}

// ---------------------------------------------------------------------------
// Status badge config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  active:      { label: 'Active',               bg: 'bg-green-100',  text: 'text-green-700' },
  suspended:   { label: 'Suspended',            bg: 'bg-yellow-100', text: 'text-yellow-700' },
  invited:     { label: 'Invited',              bg: 'bg-blue-100',   text: 'text-blue-700' },
  not_invited: { label: 'Added - Not Invited',  bg: 'bg-gray-100',   text: 'text-gray-600' },
  deleted:     { label: 'Deleted',              bg: 'bg-red-100',    text: 'text-red-700' },
};

function formatStatusLabel(s: string): string {
  const cfg = STATUS_CONFIG[s];
  return cfg?.label ?? s.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const COLUMNS: DataGridColumn<SystemUser>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (r) => `${r.firstName} ${r.lastName}`,
    sortable: true,
    filterable: true,
    minWidth: 200,
    cell: ({ row }) => {
      const u = row.original;
      return (
        <div className="flex items-center gap-3">
          <div className="shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">
            {(u.firstName?.[0] || '').toUpperCase()}{(u.lastName?.[0] || '').toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{u.firstName} {u.lastName}</p>
            <p className="text-xs text-gray-400 truncate">{u.email}</p>
          </div>
        </div>
      );
    },
  },
  {
    id: 'systemRole',
    header: 'System Role',
    accessorFn: (r) => r.systemRole?.roleName || '',
    sortable: true,
    filterable: true,
    width: 150,
    cell: ({ getValue }) => {
      const val = getValue() as string;
      if (!val) return <span className="text-sm text-gray-300">&mdash;</span>;
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-700 border border-orange-200">
          <Shield className="h-3 w-3" />{val}
        </span>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: (r) => deriveUserStatus(r),
    sortable: true,
    filterable: true,
    width: 140,
    cell: ({ getValue }) => {
      const status = getValue() as string;
      const cfg = STATUS_CONFIG[status];
      if (!cfg) return <span className="text-xs text-gray-500">{status}</span>;
      return (
        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${cfg.bg} ${cfg.text}`}>
          {cfg.label}
        </span>
      );
    },
  },
  {
    id: 'lastLogin',
    header: 'Last Login',
    accessorFn: (r) => r.lastLoginAt || '',
    sortable: true,
    width: 120,
    cell: ({ getValue }) => {
      const d = getValue() as string;
      if (!d) return <span className="text-sm text-gray-300">Never</span>;
      return <span className="text-sm text-gray-600">{new Date(d).toLocaleDateString()}</span>;
    },
  },
  {
    id: 'createdAt',
    header: 'Created',
    accessorFn: (r) => r.createdAt || '',
    sortable: true,
    width: 110,
    hidden: true,
    cell: ({ getValue }) => {
      const d = getValue() as string;
      if (!d) return <span className="text-sm text-gray-300">&mdash;</span>;
      return <span className="text-sm text-gray-500">{new Date(d).toLocaleDateString()}</span>;
    },
  },
];

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

const FILTERS: DataGridFilter[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'status',
    options: [
      { value: 'active',      label: 'Active',              color: 'green' },
      { value: 'invited',     label: 'Invited',             color: 'blue' },
      { value: 'not_invited', label: 'Added - Not Invited', color: 'gray' },
      { value: 'suspended',   label: 'Suspended',           color: 'yellow' },
      { value: 'deleted',     label: 'Deleted',             color: 'red' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Row actions
// ---------------------------------------------------------------------------

function canResendInvite(u: SystemUser): boolean {
  return deriveUserStatus(u) === 'invited';
}

function canSendInvite(u: SystemUser): boolean {
  return deriveUserStatus(u) === 'not_invited';
}

function makeRowActions(
  onView: (u: SystemUser) => void,
  onEdit: (u: SystemUser) => void,
  onSend: (u: SystemUser) => void,
  onResend: (u: SystemUser) => void,
): DataGridRowAction<SystemUser>[] {
  return [
    { id: 'view', label: 'View Details', icon: Eye, onClick: onView },
    { id: 'edit', label: 'Edit', icon: Pencil, onClick: onEdit },
    {
      id: 'send',
      label: 'Send Invitation',
      icon: Send,
      onClick: onSend,
      hidden: (u) => !canSendInvite(u),
    },
    {
      id: 'resend',
      label: 'Resend Invitation',
      icon: RefreshCw,
      onClick: onResend,
      hidden: (u) => !canResendInvite(u),
    },
  ];
}

// ---------------------------------------------------------------------------
// Seed grid templates
// ---------------------------------------------------------------------------

const NOW = new Date().toISOString();

const SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'seed-sys-users-all',
    gridType: 'system-users',
    name: 'System Users',
    isDefault: true,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['name', 'systemRole', 'status', 'lastLogin'],
      sorting: [{ id: 'name', desc: false }],
      filterValues: {},
      pageSize: 25,
      searchText: '',
    },
  },
  {
    _id: 'seed-sys-users-active',
    gridType: 'system-users',
    name: 'Active System Users',
    isDefault: false,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['name', 'systemRole', 'status', 'lastLogin'],
      sorting: [{ id: 'lastLogin', desc: true }],
      filterValues: { status: 'active' },
      pageSize: 25,
      searchText: '',
    },
  },
  {
    _id: 'seed-sys-users-admins',
    gridType: 'system-users',
    name: 'System Admins',
    isDefault: false,
    scope: 'system',
    createdBy: { userId: 'system', displayName: 'System' },
    usageCount: 0,
    createdAt: NOW,
    updatedAt: NOW,
    state: {
      columnVisibility: {},
      columnOrder: ['name', 'systemRole', 'status', 'lastLogin'],
      sorting: [{ id: 'name', desc: false }],
      filterValues: {},
      pageSize: 25,
      searchText: '',
    },
  },
];

// ---------------------------------------------------------------------------
// Feedback helpers
// ---------------------------------------------------------------------------

type InviteFeedback = { variant: 'success' | 'warning' | 'error'; message: string };

function buildBulkFeedback(
  results: Array<{ ok: boolean; error?: string }>,
  action: 'sent' | 'resent',
): InviteFeedback {
  const ok = results.filter((r) => r.ok).length;
  const failed = results.length - ok;
  if (failed === 0) {
    return { variant: 'success', message: `Invitation emails ${action} to ${ok} user${ok === 1 ? '' : 's'}.` };
  }
  if (ok === 0) {
    return { variant: 'error', message: `Failed to ${action === 'sent' ? 'send' : 'resend'} invitations for ${failed} user${failed === 1 ? '' : 's'}.` };
  }
  return { variant: 'warning', message: `${ok} ${action}, ${failed} failed.` };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inviteFeedback, setInviteFeedback] = useState<InviteFeedback | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listSystemUsers()
      .then((data) => setUsers(data as SystemUser[]))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const unsubscribe = watchSystemUsers(
      (data) => {
        setUsers(data as SystemUser[]);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [load]);

  useEffect(() => {
    if (!selectedUser) return;
    const nextSelectedUser = users.find((user) => user._id === selectedUser._id) || null;
    if (nextSelectedUser && nextSelectedUser !== selectedUser) {
      setSelectedUser(nextSelectedUser);
    }
  }, [selectedUser, users]);

  const handleUserUpdated = useCallback(() => {
    load();
  }, [load]);

  const handleModalSaved = useCallback((savedUser: SystemUser, meta?: { invited: boolean }) => {
    setEditingUser(null);
    setShowCreateModal(false);
    setSelectedUser(savedUser);
    if (meta?.invited) {
      const name = userName(savedUser);
      setInviteFeedback({
        variant: 'warning',
        message: `${name} has been added. Their invitation email is on its way.`,
      });
    }
    load();
  }, [load]);

  const handleBackgroundInviteResult = useCallback((result: BackgroundInviteResult) => {
    if (result.ok && result.user) {
      const name = userName(result.user);
      setInviteFeedback({ variant: 'success', message: `Invitation sent to ${name}.` });
      setSelectedUser((prev) => (prev && prev._id === result.user._id ? result.user : prev));
    } else {
      setInviteFeedback({
        variant: 'warning',
        message: result.error
          ?? 'The user was added, but the invitation could not be sent. Use "Resend Invitation" on the user row to retry.',
      });
    }
  }, []);

  // --- Single row: Send invitation (for not_invited users) ---
  const handleSendInvite = useCallback(async (user: SystemUser) => {
    const name = userName(user);
    try {
      const updated = await sendSystemUserInvitation(user._id);
      if (updated) {
        setUsers((prev) => prev.map((u) => u._id === updated._id ? updated : u));
        setSelectedUser((prev) => prev?._id === updated._id ? { ...updated } : prev);
      }
      setInviteFeedback({ variant: 'success', message: `Invitation sent to ${name}.` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send invitation.';
      setInviteFeedback({ variant: 'error', message: `Could not send invitation to ${name}: ${msg}` });
    }
  }, []);

  // --- Single row: Resend invitation ---
  const handleResendInvite = useCallback(async (user: SystemUser) => {
    const name = userName(user);
    const result = await resendSystemInvitation(user);
    if (result.ok) {
      setInviteFeedback({ variant: 'success', message: `Invitation resent to ${name}.` });
    } else {
      setInviteFeedback({
        variant: 'warning',
        message: result.error ?? `Could not resend the invitation to ${name}. Please try again.`,
      });
    }
  }, []);

  const rowActions = useMemo(
    () => makeRowActions(
      (u) => setSelectedUser(u),
      (u) => setEditingUser(u),
      (u) => { void handleSendInvite(u); },
      (u) => { void handleResendInvite(u); },
    ),
    [handleSendInvite, handleResendInvite],
  );

  // --- Bulk Actions (mirrors tenant-web) ---
  const bulkActions: BulkAction<SystemUser>[] = useMemo(() => [
    {
      id: 'send-selected',
      label: 'Send Invitations',
      icon: Send,
      onClick: (rows) => {
        const targets = rows.filter((r) => canSendInvite(r));
        if (targets.length === 0) return;
        void bulkSendInvitations(targets.map((r) => r._id)).then((results) => {
          setInviteFeedback(buildBulkFeedback(results, 'sent'));
          load();
        });
      },
    },
    {
      id: 'resend-selected',
      label: 'Resend Invitations',
      icon: RefreshCw,
      onClick: (rows) => {
        const targets = rows.filter((r) => canResendInvite(r));
        if (targets.length === 0) return;
        void bulkResendInvitations(targets).then((results) => {
          setInviteFeedback(buildBulkFeedback(results, 'resent'));
          load();
        });
      },
    },
  ], [load]);

  const toolbarActions = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setShowCreateModal(true)}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
      >
        <Plus className="h-4 w-4" /> New System User
      </button>
    </div>
  );

  const selectedUserName = selectedUser
    ? userName(selectedUser)
    : undefined;

  return (
    <>
      {inviteFeedback && (
        <div
          className={`flex items-start gap-2 px-4 py-2.5 mb-3 rounded-lg border text-sm ${
            inviteFeedback.variant === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : inviteFeedback.variant === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          {inviteFeedback.variant === 'success'
            ? <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
            : <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />}
          <span className="flex-1">{inviteFeedback.message}</span>
          <button
            type="button"
            onClick={() => setInviteFeedback(null)}
            className="opacity-60 hover:opacity-100"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <GridPageShell<SystemUser>
        gridId="system-users"
        gridType="system-users"
        title="System Users"
        subtitle="Platform-only administrator accounts"
        columns={COLUMNS}
        data={users}
        getRowId={(row) => row._id}
        rowActions={rowActions}
        bulkActions={bulkActions}
        filters={FILTERS}
        seedTemplates={SEED_TEMPLATES}
        onRowClick={(row) => setSelectedUser(row)}
        activeRowId={selectedUser?._id ?? null}
        toolbarActions={toolbarActions}
        searchPlaceholder="Search system users…"
        exportable
        selectable
        emptyIcon={Users}
        emptyTitle="No system users yet"
        emptySubtitle="Create a platform system user to get started"
        emptyCTA={{ label: 'New System User', onClick: () => setShowCreateModal(true) }}
        panelContent={
          selectedUser ? (
            <UserSlideoutPanel
              user={selectedUser}
              onUserUpdated={handleUserUpdated}
            />
          ) : null
        }
        panelTitle={selectedUserName}
        panelSubtitle={selectedUser?.email}
        onOpenFullPage={selectedUser ? () => navigate(`/users/${selectedUser._id}`) : undefined}
        onPanelClose={() => setSelectedUser(null)}
        onRefresh={load}
      />

      {(showCreateModal || editingUser) && (
        <EditSystemUserModal
          user={editingUser}
          onClose={() => {
            setEditingUser(null);
            setShowCreateModal(false);
          }}
          onSaved={handleModalSaved}
          onBackgroundInviteResult={handleBackgroundInviteResult}
        />
      )}
    </>
  );
}
